// @file For rendering Tldraw canvas in Vue component
import environment from '@@/bits/environment'
import { isDebugMode } from '@@/bits/flip'
import window from '@@/bits/global'
import { assetStore } from '@@/bits/tldraw/asset_store'
import Background from '@@/bits/tldraw/Background'
import EraserTool from '@@/bits/tldraw/EraserTool'
import ErrorFallback from '@@/bits/tldraw/ErrorFallback'
import InteractTool from '@@/bits/tldraw/InteractTool'
import OnTheCanvas from '@@/bits/tldraw/OnTheCanvas'
import PadletAttachmentShapeUtil from '@@/bits/tldraw/PadletAttachmentShape'
import PadletHalfEllipseShapeUtil from '@@/bits/tldraw/PadletHalfEllipseShape'
import PadletJamboardDrawGroupShapeUtil from '@@/bits/tldraw/PadletJamboardDrawGroupShape'
import { PadletSpeechBubbleTool } from '@@/bits/tldraw/PadletSpeechBubbleTool'
import { PadletSpeechBubbleUtil } from '@@/bits/tldraw/PadletSpeechBubbleUtil'
import { ShapeIndicator } from '@@/bits/tldraw/ShapeIndicator'
import { TldrawHandles } from '@@/bits/tldraw/TldrawHandles'
import { createBridgeToReactState, type BridgedStateHook } from '@@/bits/vue_react_bridge'
import { MAX_FRAME_LIMIT } from '@@/bits/whiteboard'
import { ShownCursorType } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useTldrawCollaborationStatus } from '@@/reacthooks/useTldrawCollaborationStatus'
import { WhiteboardAccessTokenApi } from '@@/surface/api/whiteboard_access_token'
import { useTldrawCollaboratorComponents } from '@@/vuecomposables/useTldrawCollaboratorComponents'
import { useSync } from '@tldraw/sync'
import { storeToRefs } from 'pinia'
import { useCallback, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import type { TLComponents, TLRecord, TLShapeId } from 'tldraw'
import {
  defaultBindingUtils,
  DefaultHandle,
  DefaultShapeIndicators,
  defaultShapeUtils,
  Editor,
  getDefaultUserPresence,
  Tldraw,
  useMenuClipboardEvents,
} from 'tldraw'
import { computed, onMounted } from 'vue'

// Change this value if you want to test with a remote server, which is needed for functionality like remakes.
// Note that the storage is shared so it may clash with other local envs with the same wall id.
const USE_REMOTE_DEV_WORKER = false

const HOST = (() => {
  const searchParams = new URLSearchParams(window.location.search)
  const whiteboardServerHostFromSearchParams = searchParams.get('whiteboard_server_host')

  switch (environment) {
    case 'production': {
      if (isDebugMode && whiteboardServerHostFromSearchParams != null) {
        return whiteboardServerHostFromSearchParams
      }
      const isBeta = window.location.hostname.endsWith('beta.padlet.com')
      return isBeta ? 'https://whiteboard-server-beta.padlet.com' : 'https://whiteboard-server.padlet.com'
    }
    case 'staging': {
      // We don't have a specific server for staging right now
      return whiteboardServerHostFromSearchParams ?? ''
    }
    case 'e2e_test':
    case 'e2e_ci': {
      return 'https://whiteboard-server-e2e.padlet.test'
    }
    case 'development':
    default:
      return USE_REMOTE_DEV_WORKER
        ? 'https://whiteboard_server_dev.padlet.workers.dev'
        : 'https://whiteboard-server.padletdev.com'
  }
})()

/**
 * Whitelisted domains:
 * - padlet.com
 * - padlet.dev
 * - *.padletorg.dev
 * - *.padlet.org
 * - *.wallwisher.net
 */
const TLDRAW_LICENSE_KEY =
  'tldraw-padlet--wallwisher-inc--2025-05-31/WyIzMWJ2OEF4MSIsWyIqLnBhZGxldC5jb20iLCIqLnBhZGxldC5vcmciLCIqLnBhZGxldC5kZXYiLCIqLnBhZGxldG9yZy5kZXYiLCIqLndhbGx3aXNoZXIubmV0Il0sMSwiMjAyNS0wNS0zMSJd.znJDtVhsQzUmaR+TjvlxNegniiZwOv+6Q+LXztWXe1RStbdYymzJ+S055ZrZ8uc3r3/T9XmhnpeHxR239yrDxg'

// NOTE: These constants must always be static. If we put them inside DrawingCanvas,
// their references become unstable and Tldraw will rerender the whole canvas.
const CUSTOM_SHAPE_UTILS = [
  PadletAttachmentShapeUtil,
  PadletHalfEllipseShapeUtil,
  PadletJamboardDrawGroupShapeUtil,
  PadletSpeechBubbleUtil,
]

const CUSTOM_AND_DEFAULT_SHAPE_UTILS = [...CUSTOM_SHAPE_UTILS, ...defaultShapeUtils]

const CUSTOM_TOOLS = [EraserTool, InteractTool, PadletSpeechBubbleTool]

const TLDRAW_COMPONENTS: TLComponents = {
  ShapeIndicator,
  ShapeIndicators: DefaultShapeIndicators, // TLdrawShapeIndicators has logic that we don't need https://github.com/tldraw/tldraw/pull/4083/files, we render DefaultShapeIndicators directly
  Handles: TldrawHandles,
  Handle: DefaultHandle,
  ContextMenu: null,
  Background,
  // OnTheCanvas ensures the component will scale with the camera zoom, while also not being affected by the tools
  OnTheCanvas,
  ErrorFallback,
  ...useTldrawCollaboratorComponents(),
}

const DrawingCanvas = ({
  onMount,
  wallId,
  wallHashid,
  canIViewShape,
  useShownCursorType,
}: {
  wallId: string
  wallHashid: string
  onMount: (editor: Editor) => void
  canIViewShape: (record: TLRecord) => boolean
  useShownCursorType: BridgedStateHook<ShownCursorType>
}): React.JSX.Element => {
  const [shownCursorType] = useShownCursorType()

  // The uri callback is a function instead of a string to ensure the access token
  // is retrieved on-demand rather than at a fixed time, allowing us to generate a fresh token
  // each time we try to connect to the whiteboard.
  const getConnectionUri = useCallback(async () => {
    const url = new URL(`${HOST}/connect/${wallId}`)
    const accessToken = await WhiteboardAccessTokenApi.fetch(wallHashid)
    url.searchParams.set('accessToken', accessToken)
    return url.toString()
  }, [wallId, wallHashid])

  const getUserPresence = useCallback<typeof getDefaultUserPresence>(
    (store, user) => {
      if (shownCursorType === ShownCursorType.NONE) return null
      return getDefaultUserPresence(store, user)
    },
    [getDefaultUserPresence, shownCursorType],
  )

  const store = useSync({
    uri: getConnectionUri,
    assets: assetStore,
    shapeUtils: CUSTOM_AND_DEFAULT_SHAPE_UTILS,
    bindingUtils: defaultBindingUtils,
    getUserPresence,
  })

  // This function handles hiding unapproved shapes from those who shouldn't be able to see it
  // because as of v2.4, Tldraw does not have any built-in method to control shape rendering.
  useEffect(() => {
    /**
     * DISABLE UNAPPROVED SHAPE SELECTION OUTLINE (BY COLLABORATORS)
     */

    const _superGetCollaborators = Editor.prototype.getCollaborators
    Editor.prototype.getCollaborators = function () {
      const collaborators = _superGetCollaborators.call(this)
      return collaborators.map((c) => ({
        ...c,
        selectedShapeIds: c.selectedShapeIds.filter((id) => {
          const shape = this.getShape(id)
          return shape != null && canIViewShape(shape)
        }),
      }))
    }

    /**
     * DISABLE UNAPPROVED SHAPE RENDERING
     */

    const _superGetCurrentPageShapeIds = Editor.prototype.getCurrentPageShapeIds
    Editor.prototype.getCurrentPageShapeIds = function () {
      const ids = _superGetCurrentPageShapeIds.call(this)
      return new Set(
        [...ids].filter((id) => {
          const shape = this.getShape(id)
          return shape != null && canIViewShape(shape)
        }),
      )
    }

    const _superGetSortedChildIdsForParent = Editor.prototype.getSortedChildIdsForParent
    Editor.prototype.getSortedChildIdsForParent = function (parentId: TLShapeId) {
      const ids = _superGetSortedChildIdsForParent.call(this, parentId)
      return ids.filter((id) => {
        const shape = this.getShape(id)
        return shape != null && canIViewShape(shape)
      })
    }
  }, [canIViewShape])

  return (
    <Tldraw
      // Explicitly do not pass anything to assetUrls prop. Instead, we override the defaults using patch-package.
      // There is a weird quirk with our setup that causes Tldraw to unmount and remount when this prop is passed.
      // I was not able to replicate it in a regular React app setup or by tracing the code, so for now this prop is not passed.
      // Priority is to not have to deal with the component mounting twice which can cause all sorts of issues.
      licenseKey={TLDRAW_LICENSE_KEY}
      shapeUtils={CUSTOM_SHAPE_UTILS}
      onMount={onMount}
      store={store}
      tools={CUSTOM_TOOLS}
      components={TLDRAW_COMPONENTS}
      options={{
        // Tldraw default is 40
        maxPages: MAX_FRAME_LIMIT,
      }}
      hideUi
    >
      <SetupHooks />
    </Tldraw>
  )
}

/**
 * Most of the hooks that tldraw provides require the editor to be loaded.
 * Since hooks can't be called conditionally, and we use Vue everywhere else,
 * the easiest way to call tldraw hooks is to call them in <Tldraw /> children.
 */
const SetupHooks = (): React.JSX.Element => {
  useTldrawCollaborationStatus()
  const menuClipboardEvents = useMenuClipboardEvents()

  useEffect(() => {
    window.tldrawMenuClipboardEvents = menuClipboardEvents
  }, [])

  return <></>
}

export const useTldrawCanvas = ({
  elementId,
  onMount,
}: {
  elementId: string
  onMount: (editor: Editor) => void
}): void => {
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const { wallId, wallHashidWithoutPrefix } = storeToRefs(useSurfaceStore())
  const { isReadOnlyMode } = storeToRefs(useWhiteboardModeStore())
  const { shownCursorType } = storeToRefs(useWhiteboardCollaborationStore())

  const bridgedShownCursorType = createBridgeToReactState(
    computed<ShownCursorType>(() => {
      if (isReadOnlyMode.value) return ShownCursorType.NONE
      return shownCursorType.value
    }),
  )

  onMounted(() => {
    if (elementId == null) {
      throw new Error('Element id is required')
    }
    const domNode = document.getElementById(elementId) as HTMLElement
    if (domNode == null) {
      throw new Error(`Element with id ${elementId} not found`)
    }
    const root = createRoot(domNode)
    root.render(
      <DrawingCanvas
        onMount={onMount}
        wallId={wallId.value.toString()}
        wallHashid={wallHashidWithoutPrefix.value.toString()}
        canIViewShape={whiteboardPermissionsStore.canIViewShape}
        useShownCursorType={bridgedShownCursorType}
      />,
    )
  })
}
